/**
 * Runtime SPA configuration getters.
 *
 * The SPA reads its API origins + API key + Cognito coordinates at
 * boot from `/config.json`. The bootstrap step in `main.tsx`
 * fetches that file and assigns the parsed object to
 * `globalThis.__DVGUP_CONFIG__` before the React tree mounts; every
 * subsequent consumer reads through the getters below so the values
 * flow from one canonical source.
 *
 * Function-length budget: every export stays under 20 lines per
 * Requirement 3.7.
 */

/**
 * Public-website runtime config injected at SPA bootstrap.
 */
export interface PublicWebsiteConfig {
  /**
   * Origin for the public REST API. Production: `/api` (CloudFront
   * routes via the additional cache behavior). Local dev: the dev-
   * server proxy origin from `vite.config.ts`.
   */
  readonly publicApiInvokeUrl: string;
  /**
   * Public REST API key sourced from the API Gateway usage plan,
   * injected as the `x-api-key` header by the http-client wrapper.
   */
  readonly publicApiKey: string;
}

/**
 * Read the public-website runtime config injected by `/config.json`.
 *
 * @returns The injected `PublicWebsiteConfig`, or a placeholder
 *   `{ publicApiInvokeUrl: '/api', publicApiKey: '' }` when the
 *   bootstrap has not run yet.
 */
export function getPublicWebsiteConfig(): PublicWebsiteConfig {
  const fromWindow = (
    globalThis as unknown as { __DVGUP_CONFIG__?: PublicWebsiteConfig }
  ).__DVGUP_CONFIG__;
  return fromWindow ?? { publicApiInvokeUrl: '/api', publicApiKey: '' };
}

/**
 * Admin gateway coordinates. Round-22 reconciliation: admin
 * endpoints are called directly against the `AdminApiInvokeUrl`
 * from `/config.json` (CORS enabled on the admin gateway for the
 * dvgup.com origins). No API key — the Cognito ID token in the
 * `Authorization` header is the auth boundary.
 */
export interface AdminApiConfig {
  readonly adminApiInvokeUrl: string;
}

export function getAdminApiConfig(): AdminApiConfig {
  const fromWindow = (
    globalThis as unknown as {
      __DVGUP_CONFIG__?: { adminApiInvokeUrl?: string };
    }
  ).__DVGUP_CONFIG__;
  return { adminApiInvokeUrl: fromWindow?.adminApiInvokeUrl ?? '' };
}
